import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import { ErrorBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import { deriveFrozenPricingStatuses, frozenPricingStatusUnavailable } from '../../lib/frozenPricingStatus'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

// =======================================================================
// Unpriced/Incomplete Items — Wave 4.1 "Estimating Intelligence" layer.
// Read-only. Every status shown here is classified directly from
// boq_item_pricing_status, which itself reuses the existing calculation
// chain (rate_buildup_line_cost, boq_item_selling_price) as its sole
// source of truth. No pricing math happens in this component.
// =======================================================================

const STATUS_CONFIG = {
  unpriced: { label: 'Unpriced', bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B', icon: '○' },
  incomplete: { label: 'Incomplete / Rate Missing', bg: '#FFF7ED', border: '#FDBA74', text: '#9A3412', icon: '⚠' },
  zero_priced: { label: 'Zero Priced', bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', icon: '0' },
  priced: { label: 'Priced', bg: '#F0FDF4', border: '#86EFAC', text: '#166534', icon: '✓' },
  excluded: { label: 'Excluded', bg: '#F3F4F6', border: '#D1D5DB', text: '#6B7280', icon: '—' },
}

const FILTERABLE_STATUSES = ['unpriced', 'incomplete', 'zero_priced', 'priced']

export default function UnpricedItemsPage() {
  const { tenderId, revisionId } = useParams()
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [activeFilters, setActiveFilters] = useState(new Set(['unpriced', 'incomplete', 'zero_priced']))
  const [showExcluded, setShowExcluded] = useState(false)

  const load = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    if (revRes.error) return setError(revRes.error.message)

    if (revRes.data?.status === 'frozen') {
      // Frozen: boq_item_pricing_status returns nothing once the live
      // source tables are empty post-freeze (proven directly). For a
      // future frozen revision, the same classification can now be
      // derived safely from the immutable full_boq_data +
      // full_indirect_data.rate_buildup_lines (captured at freeze time
      // by the updated freeze_estimate_revision) -- mirroring the live
      // View's own CASE exactly:
      //   is_excluded -> excluded
      //   line_count = 0 -> unpriced
      //   any line missing a resolvable cost -> incomplete
      //   selling_price = 0 -> zero_priced
      //   else -> priced
      // For existing (pre-contract) frozen revisions, rate_buildup_lines
      // is an empty array (never captured) -- every non-excluded item
      // would show line_count = 0, which would misreport a possibly
      // fully-priced item as "unpriced". Rather than show a misleading
      // classification, this page shows an explicit unavailable notice
      // for those revisions instead of guessing.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('full_boq_data, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (snapRes.error) return setError(snapRes.error.message)
      if (!snapRes.data) return setError('This revision is marked frozen but no snapshot record could be found.')

      const boqData = Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []
      const indirect = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      const rateBuildupLines = Array.isArray(indirect.rate_buildup_lines) ? indirect.rate_buildup_lines : []

      if (frozenPricingStatusUnavailable(boqData, rateBuildupLines)) {
        setItems(null)
        setError('Historical Rate Build-Up detail was not captured for this frozen revision, so a complete Pricing Status classification is not available. (This revision was frozen before that data began being preserved.)')
        return
      }

      const statusById = Object.fromEntries(deriveFrozenPricingStatuses(boqData, rateBuildupLines).map((s) => [s.boq_item_id, s]))

      setItems(boqData.map((it) => {
        const s = statusById[it.id]
        return {
          boq_item_id: it.id, item_no: it.item_no, description: it.description, unit: it.unit,
          resolved_quantity: it.resolved_quantity, line_count: s.line_count, null_line_count: s.null_line_count,
          selling_price: it.selling_price, pricing_status: s.pricing_status,
        }
      }))
      return
    }

    const { data, error } = await supabase
      .from('boq_item_pricing_status')
      .select('*')
      .eq('estimate_revision_id', revisionId)
      .order('item_no')
    if (error) setError(error.message)
    else setItems(data)
  }, [revisionId])

  useEffect(() => { load() }, [load])

  function toggleFilter(status) {
    setActiveFilters((prev) => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }

  if (error) return <ErrorBanner message={error} />
  if (items === null) return <SkeletonRows rows={5} cols={6} />

  const visibleItems = items.filter((it) => {
    if (it.pricing_status === 'excluded') return showExcluded
    return activeFilters.has(it.pricing_status)
  })

  const counts = FILTERABLE_STATUSES.reduce((acc, s) => {
    acc[s] = items.filter((it) => it.pricing_status === s).length
    return acc
  }, {})
  const excludedCount = items.filter((it) => it.pricing_status === 'excluded').length

  return (
    <div>
      <EstimationBreadcrumb />

      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>
        Unpriced &amp; Incomplete Items
      </h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 20px' }}>
        Pricing completeness across all BOQ items in this revision — read directly from the existing calculation chain.
      </p>

      {/* Status filter chips, doubling as a summary count */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {FILTERABLE_STATUSES.map((status) => {
          const cfg = STATUS_CONFIG[status]
          const active = activeFilters.has(status)
          return (
            <button
              key={status}
              onClick={() => toggleFilter(status)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                border: `1.5px solid ${active ? cfg.border : colors.border}`,
                background: active ? cfg.bg : '#fff',
                opacity: active ? 1 : 0.55,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: cfg.text }}>{cfg.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: cfg.text }}>{cfg.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: cfg.text, background: '#fff', borderRadius: 10, padding: '0 6px' }}>{counts[status]}</span>
            </button>
          )
        })}
        <button
          onClick={() => setShowExcluded((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
            border: `1.5px solid ${showExcluded ? STATUS_CONFIG.excluded.border : colors.border}`,
            background: showExcluded ? STATUS_CONFIG.excluded.bg : '#fff',
            opacity: showExcluded ? 1 : 0.55,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: STATUS_CONFIG.excluded.text }}>Show Excluded</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_CONFIG.excluded.text, background: '#fff', borderRadius: 10, padding: '0 6px' }}>{excludedCount}</span>
        </button>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {visibleItems.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState title="No items match the selected filters" description="Toggle a status chip above to see items." />
          </div>
        )}
        {visibleItems.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>Item No.</Th>
                <Th>Description</Th>
                <Th>Unit</Th>
                <Th align="right">Quantity</Th>
                <Th>Status</Th>
                <Th align="right">Selling Price</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((it) => {
                const cfg = STATUS_CONFIG[it.pricing_status]
                return (
                  <tr key={it.boq_item_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Td>{it.item_no}</Td>
                    <Td>{it.description}</Td>
                    <Td muted>{it.unit}</Td>
                    <Td align="right">{formatNumber(it.resolved_quantity)}</Td>
                    <Td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 12,
                        background: cfg.bg, border: `1px solid ${cfg.border}`, fontSize: 11, fontWeight: 700, color: cfg.text,
                      }}>
                        {cfg.icon} {cfg.label}
                      </span>
                      {it.pricing_status === 'incomplete' && (
                        <div style={{ fontSize: 10, color: cfg.text, marginTop: 3 }}>
                          {it.null_line_count} of {it.line_count} line(s) cannot be priced
                        </div>
                      )}
                    </Td>
                    <Td align="right" style={it.pricing_status === 'priced' ? { fontWeight: 700, color: colors.textPrimary } : { color: colors.textMuted }}>
                      {it.selling_price != null ? `${formatNumber(it.selling_price)} SAR` : '—'}
                    </Td>
                    <Td align="right">
                      {it.pricing_status !== 'excluded' && (
                        <Link
                          to={`/estimation/tender/${tenderId}/revision/${revisionId}/rate-build-up?item=${it.boq_item_id}`}
                          style={{ fontSize: 12, fontWeight: 600, color: colors.primary, textDecoration: 'none' }}
                        >
                          Open Rate Build-Up →
                        </Link>
                      )}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted, style }) {
  return <td style={{ textAlign: align, padding: '11px 16px', color: muted ? colors.textMuted : colors.textPrimary, fontSize: 14, ...style }}>{children}</td>
}
